import React from 'react';
import makeStyles from '@material-ui/core/styles/makeStyles';
import { useTranslation } from 'next-i18next';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Slider from 'react-slick';
import cx from 'classnames';

const useStyles = makeStyles(theme => ({
  root: {
    display: 'block',
    marginTop: -90,
    [theme.breakpoints.up('md')]: {
      display: 'none',
    },
  },
  planWrapper: {
    backgroundColor: '#00A7E8',
    textAlign: 'center',
    borderRadius: 6,
    padding: '16px 10px',
    margin: '0 auto',
    minHeight: ({ language }) => (language === 'en' ? 130 : 112),
    maxWidth: 300,
    [theme.breakpoints.up('sm')]: {
      maxWidth: 'initial',
    },
  },
  planName: {
    fontSize: 20,
    color: '#fff',
    marginBottom: 8,
  },
  planDesc: {
    fontSize: 14,
    color: '#fff',
  },
  name: {
    fontSize: 14,
  },
  oddRow: {
    backgroundColor: '#F7F7F8',
  },
  tableCellHead: {
    color: '#fff',
    backgroundColor: '#828B9D',
  },
  tableRoot: {
    marginTop: 40,
  },
  tableCellBody: {
    backgroundColor: '#F7F7F8',
    minWidth: 99,
    borderBottom: 0,
  },
  tableCellBodyOdd: {
    backgroundColor: '#f2f2f2',
    borderBottom: 0,
    minWidth: 99,
  },
  tableCell: {
    backgroundColor: '#fff',
    borderBottom: 0,
    paddingLeft: 14,
  },
  tableCellOddRow: {
    backgroundColor: '#F7F7F8',
    borderBottom: 0,
    paddingLeft: 14,
  },
  registerButton: {
    backgroundColor: '#076BD1',
    borderRadius: 40,
    height: 38,
    display: 'inline-block',
    [theme.breakpoints.up('md')]: {
      height: 50,
    },
  },
  registerLabel: {
    color: '#fff',
    padding: '12px 36px',
    fontSize: 16,
    lineHeight: '19px',
    position: 'relative',
    top: 10,
    whiteSpace: 'nowrap',
    [theme.breakpoints.up('md')]: {
      top: 12,
      fontSize: 20,
      lineHeight: '24px',
    },
  },
  basicPlanWrapper: {
    backgroundColor: '#00A7E8',
  },
  storePlanWrapper: {
    backgroundColor: '#076BD1',
  },
  proPlanWrapper: {
    backgroundColor: '#0CB392',
  },
  companyPlanWrapper: {
    backgroundColor: '#8070D7',
  },
  customisePlanWrapper: {
    backgroundColor: '#828B9D',
  },
  '@global': {
    '.slick-slide': {
      padding: '0 9px',
    },
    '.slick-slide img': {
      margin: '0 auto',
    },
    '.slick-dots': {
      top: ({ language }) => (language === 'en' ? 130 : 112),
      bottom: 'initial',
    },
    '.slick-dots li button': {
      borderRadius: '50%',
      border: '1px solid #CDD1D8',
      backgroundColor: '#fff',
      width: 8,
      height: 8,
    },
    '.slick-dots li button:before': {
      color: 'transparent',
    },
    'li.slick-active button': {
      backgroundColor: '#076BD1',
      border: 0,
    },
    '.slick-dots li.slick-active button:before': {
      color: 'transparent',
    },
  },
}));

const ImageTick = () => <img width="20" src="/static/images/tick.svg" />;
const ImageCross = () => <img width="20" src="/static/images/cross.svg" />;

function createData(name, value) {
  return { name, value };
}

const MobilePricingTable = () => {
  const { t, i18n } = useTranslation('common');
  const classes = useStyles({ language: i18n.language });
  const settings = {
    dots: true,
    infinite: false,
    speed: 500,
    autoplaySpeed: 4000,
    slidesToShow: 1,
    slidesToScroll: 1,
    centerMode: true,
    adaptiveHeight: true,
    centerPadding: '20px',
  };
  const plans = [
    {
      planName: t('basic-plan'),
      planDesc: t('basic-plan-desc'),
      features: [
        createData(t('auto-order-capture'), <ImageTick />),
        createData(t('manual-order-capture'), <ImageTick />),
        createData(t('facebook-notification'), <ImageTick />),
        createData(t('split-combine-order'), <ImageTick />),
        createData(t('auto-capture-order'), <ImageTick />),
        createData(t('customer-categorization-management'), <ImageTick />),
        createData(t('feature-6'), <ImageTick />),
        createData(t('feature-9'), <ImageTick />),
        createData(t('subfeature-5'), <ImageTick />),
        createData(t('sales-data-reports'), <ImageTick />),
        createData(t('batch-print'), 10),
        createData(t('lucky-draw'), <ImageCross />),
        createData(t('feature-7'), <ImageCross />),
        createData(t('feature-2'), <ImageCross />),
        createData(t('feature-3'), <ImageCross />),
        createData(t('feature-10'), <ImageCross />),
        createData(t('multiple-account-management'), 1),
        createData(t('payment-receive-notification'), <ImageTick />),
        createData('Offline Banking', <ImageTick />),
        createData('Online Banking FPX', <ImageTick />),
        createData('E-Wallet', <ImageTick />),
      ],
    },
    {
      planName: t('store-plan'),
      planDesc: t('store-plan-desc'),
      features: [
        createData(t('auto-order-capture'), <ImageCross />),
        createData(t('manual-order-capture'), <ImageCross />),
        createData(t('facebook-notification'), <ImageTick />),
        createData(t('split-combine-order'), <ImageTick />),
        createData(t('auto-capture-order'), <ImageCross />),
        createData(t('customer-categorization-management'), <ImageTick />),
        createData(t('feature-6'), <ImageCross />),
        createData(t('feature-9'), <ImageTick />),
        createData(t('subfeature-5'), <ImageTick />),
        createData(t('sales-data-reports'), <ImageTick />),
        createData(t('batch-print'), 10),
        createData(t('lucky-draw'), <ImageCross />),
        createData(t('feature-7'), <ImageTick />),
        createData(t('feature-2'), <ImageTick />),
        createData(t('feature-3'), 1),
        createData(t('feature-10'), <ImageCross />),
        createData(t('multiple-account-management'), 1),
        createData(t('payment-receive-notification'), <ImageCross />),
        createData('Offline Banking', <ImageTick />),
        createData('Online Banking FPX', <ImageTick />),
        createData('E-Wallet', <ImageTick />),
      ],
    },
    {
      planName: t('pro-plan'),
      planDesc: t('pro-plan-desc'),
      features: [
        createData(t('auto-order-capture'), <ImageTick />),
        createData(t('manual-order-capture'), <ImageTick />),
        createData(t('facebook-notification'), <ImageTick />),
        createData(t('split-combine-order'), <ImageTick />),
        createData(t('auto-capture-order'), <ImageTick />),
        createData(t('customer-categorization-management'), <ImageTick />),
        createData(t('feature-6'), <ImageTick />),
        createData(t('feature-9'), <ImageTick />),
        createData(t('subfeature-5'), <ImageTick />),
        createData(t('sales-data-reports'), <ImageTick />),
        createData(t('batch-print'), 50),
        createData(t('lucky-draw'), <ImageTick />),
        createData(t('feature-7'), <ImageTick />),
        createData(t('feature-2'), <ImageTick />),
        createData(t('feature-3'), 1),
        createData(t('feature-10'), <ImageCross />),
        createData(t('multiple-account-management'), 3),
        createData(t('payment-receive-notification'), <ImageTick />),
        createData('Offline Banking', <ImageTick />),
        createData('Online Banking FPX', <ImageTick />),
        createData('E-Wallet', <ImageTick />),
      ],
    },
    {
      planName: t('company-plan'),
      planDesc: t('company-plan-desc'),
      features: [
        createData(t('auto-order-capture'), <ImageTick />),
        createData(t('manual-order-capture'), <ImageTick />),
        createData(t('facebook-notification'), <ImageTick />),
        createData(t('split-combine-order'), <ImageTick />),
        createData(t('auto-capture-order'), <ImageTick />),
        createData(t('customer-categorization-management'), <ImageTick />),
        createData(t('feature-6'), <ImageTick />),
        createData(t('feature-9'), <ImageTick />),
        createData(t('subfeature-5'), <ImageTick />),
        createData(t('sales-data-reports'), <ImageTick />),
        createData(t('batch-print'), t('unlimited')),
        createData(t('lucky-draw'), <ImageTick />),
        createData(t('feature-7'), <ImageTick />),
        createData(t('feature-2'), <ImageTick />),
        createData(t('feature-3'), 1),
        createData(t('feature-10'), 3),
        createData(t('multiple-account-management'), 5),
        createData(t('payment-receive-notification'), <ImageTick />),
        createData('Offline Banking', <ImageTick />),
        createData('Online Banking FPX', <ImageTick />),
        createData('E-Wallet', <ImageTick />),
      ],
    },
    {
      planName: t('customise-plan'),
      planDesc: t('customise-plan-desc'),
      features: [
        createData(
          '',
          <a
            href="https://api.whatsapp.com/send/?phone=60162433083&text=你好，想知道有关抓单系统的详情。"
            className={classes.registerButton}
          >
            <span className={classes.registerLabel}>{t('for-customization-contact-us')}</span>
          </a>
        ),
      ],
    },
  ];
  return (
    <div className={classes.root}>
      <Slider {...settings}>
        {plans.map(({ planName, planDesc, features = [] }, i) => (
          <div key={i}>
            <div
              className={cx(classes.planWrapper, {
                [classes.basicPlanWrapper]: i === 0,
                [classes.storePlanWrapper]: i === 1,
                [classes.proPlanWrapper]: i === 2,
                [classes.companyPlanWrapper]: i === 3,
                [classes.customisePlanWrapper]: i === 4,
              })}
            >
              <div className={classes.planName}>{planName}</div>
              <div className={classes.planDesc}>{planDesc}</div>
            </div>
            <Table classes={{ root: classes.tableRoot }} sx={{ minWidth: 650 }}>
              <TableHead>
                <TableRow>
                  <TableCell align="center" colSpan="2" classes={{ head: classes.tableCellHead }}>
                    {t('features')}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {features.map((row, index) => (
                  <TableRow
                    className={cx('', { [classes.oddRow]: i % 2 === 0 })}
                    key={index}
                    sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                  >
                    {i !== 4 ? (
                      <TableCell
                        component="th"
                        classes={{
                          body: cx([classes.tableCell], {
                            [classes.tableCellOddRow]: index % 2 === 0,
                          }),
                        }}
                      >
                        <span className={classes.name}>{row.name}</span>
                      </TableCell>
                    ) : null}
                    <TableCell
                      align="center"
                      component="th"
                      colSpan={index === 4 ? 2 : 0}
                      classes={{
                        body: index % 2 ? classes.tableCellBody : classes.tableCellBodyOdd,
                      }}
                    >
                      <span className={classes.planValue}>{row.value}</span>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ))}
      </Slider>
    </div>
  );
};

export default MobilePricingTable;
